// ABOUTME: Social login buttons component with Google and Facebook OAuth integration
// ABOUTME: Converted from Tailwind CSS to Material-UI for design system consistency

import React, { useState, useEffect } from 'react';
import { Button, Stack, CircularProgress } from '@mui/material';
import { oauthBrandColors } from '../theme/tokens';

interface SocialLoginButtonsProps {
  isLoading?: boolean;
  onSocialLogin?: (provider: string) => void;
  onAvailabilityChange?: (hasAvailableProviders: boolean) => void;
}

interface AvailableProvidersResponse {
  availableProviders: {
    google: boolean;
    facebook: boolean;
  };
}

// Brand-compliant icon components for consistent styling and easier maintenance
// Inline brand SVG markup — not an MUI/lucide component, sx doesn't apply
const GoogleIcon: React.FC = () => (
  <svg style={{ width: 20, height: 20 }} viewBox="0 0 24 24">
    <path
      fill={oauthBrandColors.google}
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill={oauthBrandColors.googleGreen}
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill={oauthBrandColors.googleYellow}
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill={oauthBrandColors.googleRed}
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const FacebookIcon: React.FC = () => (
  <svg style={{ width: 20, height: 20 }} fill={oauthBrandColors.facebook} viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
  isLoading = false,
  onSocialLogin,
  onAvailabilityChange
}) => {
  const [availableProviders, setAvailableProviders] = useState<{ google: boolean; facebook: boolean } | null>(null);
  const [fetchingProviders, setFetchingProviders] = useState(true);
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  // Helper to construct API URL with proper /api prefix
  const getApiUrl = () => API_BASE_URL.includes('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

  // Fetch available providers on component mount
  useEffect(() => {
    const fetchAvailableProviders = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/auth/available-providers`);

        if (!response.ok) {
          // Fail closed - hide all providers if API fails
          setAvailableProviders({ google: false, facebook: false });
          onAvailabilityChange?.(false);
          return;
        }

        const data: AvailableProvidersResponse = await response.json();
        setAvailableProviders(data.availableProviders);

        // Notify parent about availability
        const hasAvailableProviders = data.availableProviders.google || data.availableProviders.facebook;
        onAvailabilityChange?.(hasAvailableProviders);
      } catch (error) {
        // Fail closed - hide all providers if API fails
        console.error('Failed to fetch available auth providers:', error);
        const fallbackProviders = { google: false, facebook: false };
        setAvailableProviders(fallbackProviders);

        // Notify parent that no providers are available
        onAvailabilityChange?.(false);
      } finally {
        setFetchingProviders(false);
      }
    };

    fetchAvailableProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getApiUrl intentionally omitted; it is stable and adding it would cause extra fetches
  }, [API_BASE_URL, onAvailabilityChange]);

  // Generic handler for social provider login to reduce code duplication
  const handleProviderLogin = (provider: 'google' | 'facebook') => {
    if (onSocialLogin) {
      onSocialLogin(provider);
    } else {
      // If we're on the Alexa linking page, store the auth code in sessionStorage
      // so AuthCallback can complete the Alexa linking after OAuth succeeds.
      // This avoids passing the auth code through URL params (browser history exposure).
      const searchParams = new URLSearchParams(window.location.search);
      const isAlexaLinking = searchParams.get('alexa_linking') === 'true';
      const alexaAuthCode = searchParams.get('auth_code');
      if (isAlexaLinking && alexaAuthCode) {
        sessionStorage.setItem('alexa_pending_auth_code', alexaAuthCode);
      }

      // Get current URL including any Alexa linking parameters
      const currentUrl = `${window.location.pathname}${window.location.search}`;
      const returnUrl = encodeURIComponent(currentUrl);
      const redirectUrl = `${getApiUrl()}/auth/oauth/${provider}?returnUrl=${returnUrl}`;

      // Use more reliable redirect method for mobile browsers (especially in-app browsers)
      try {
        // Try form submission first (most reliable on mobile)
        const form = document.createElement('form');
        form.method = 'GET';
        form.action = redirectUrl;
        form.style.display = 'none';
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      } catch {
        // Fallback to direct redirect
        window.location.href = redirectUrl;
      }
    }
  };

  // Show loading spinner while fetching providers
  if (fetchingProviders) {
    return (
      <Stack spacing={2} data-testid="social-login-stack" alignItems="center">
        <CircularProgress size={24} role="progressbar" />
      </Stack>
    );
  }

  // Render only available provider buttons
  return (
    <Stack spacing={2} data-testid="social-login-stack">
      {availableProviders?.google && (
        <Button
          type="button"
          onClick={() => handleProviderLogin('google')}
          disabled={isLoading}
          variant="outlined"
          fullWidth
          startIcon={<GoogleIcon />}
          aria-label="Continue with Google"
        >
          Continue with Google
        </Button>
      )}

      {availableProviders?.facebook && (
        <Button
          type="button"
          onClick={() => handleProviderLogin('facebook')}
          disabled={isLoading}
          variant="outlined"
          fullWidth
          startIcon={<FacebookIcon />}
          aria-label="Continue with Facebook"
        >
          Continue with Facebook
        </Button>
      )}
    </Stack>
  );
};

export default SocialLoginButtons;